/** Signup page visual system — Plus Jakarta Sans, same stack as the logged-in app. */

import { APP_FONT_FAMILY } from "../constants/typography";
import { BRAND_PRIMARY } from "../constants/brandColors";
import { getMm } from "../theme/modernMaterialEnterprise";

export const SIGNUP_FONT = APP_FONT_FAMILY;

export const signupPageOuterSx = (isDarkMode) => ({
  width: "100%",
  minHeight: { xs: "100dvh", sm: "100vh" },
  backgroundColor: getMm(isDarkMode).background,
  fontFamily: SIGNUP_FONT,
  overflowX: "hidden",
});

export const signupPageContainerSx = {
  minHeight: { xs: "100dvh", sm: "100vh" },
  display: "flex",
  flexDirection: "column",
  justifyContent: { xs: "flex-start", sm: "center" },
  alignItems: "center",
  px: { xs: 1, sm: 3 },
  pt: {
    xs: "calc(env(safe-area-inset-top, 0px) + 56px)",
    sm: "calc(env(safe-area-inset-top, 0px) + 72px)",
  },
  pb: {
    xs: "calc(env(safe-area-inset-bottom, 0px) + 28px)",
    sm: 4,
  },
  position: "relative",
  zIndex: 1,
  boxSizing: "border-box",
  width: "100%",
};

export const signupContentColumnSx = {
  width: "100%",
  maxWidth: { xs: "100%", sm: 560, md: 680, lg: 760 },
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
};

export const signupCardContentSx = {
  p: { xs: 2, sm: 3, md: 4 },
  "&:last-child": { pb: { xs: 2, sm: 3, md: 4 } },
};

/** Full-width form grid on narrow screens — minimal gutters, stretch children. */
export const signupFormGridSx = {
  width: "100%",
  maxWidth: "100%",
  mx: 0,
  "& > .MuiGrid-item": {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
  },
};

export function signupLogoSx() {
  return {
    display: "block",
    mx: "auto",
    width: "100%",
    maxWidth: { xs: 200, sm: 240 },
    height: "auto",
    objectFit: "contain",
    filter: "none",
    boxShadow: "none",
  };
}

export const signupCardShellSx = (isDarkMode) => ({
  borderRadius: "8px",
  boxShadow: "none",
  border: `1px solid ${getMm(isDarkMode).outlineVariant}`,
  overflow: "hidden",
  backgroundColor: getMm(isDarkMode).surfaceLowest,
  fontFamily: SIGNUP_FONT,
  width: "100%",
});

export function signupFieldSx(isDarkMode) {
  return {
    width: "100%",
    maxWidth: "100%",
    display: "block",
    fontFamily: SIGNUP_FONT,
    "& .MuiOutlinedInput-root": {
      width: "100%",
      borderRadius: "8px",
      backgroundColor: getMm(isDarkMode).surfaceLowest,
      fontFamily: SIGNUP_FONT,
      minHeight: { xs: 48, sm: "auto" },
      pl: { xs: 1.25, sm: 1.5 },
      pr: { xs: 1.25, sm: 1.5 },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: getMm(isDarkMode).outlineVariant,
        borderWidth: "2px",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: getMm(isDarkMode).outlineVariant,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: getMm(isDarkMode).outlineVariant,
        borderWidth: "2px",
      },
      "& .MuiInputAdornment-positionStart": {
        marginRight: { xs: 0.5, sm: 0.75 },
        display: { xs: "none", sm: "flex" },
      },
      "& .MuiInputAdornment-positionEnd": {
        marginLeft: { xs: 0.25, sm: 0.5 },
      },
      "& .MuiIconButton-root": {
        padding: { xs: 0.5, sm: 0.75 },
      },
      "& input, & textarea": {
        width: "100%",
        color: getMm(isDarkMode).onSurface,
        fontFamily: SIGNUP_FONT,
        fontSize: { xs: "1rem", sm: "0.875rem" },
        py: { xs: 1.25, sm: undefined },
      },
    },
    "& .MuiInputLabel-root": {
      color: getMm(isDarkMode).onSurfaceVariant,
      fontFamily: SIGNUP_FONT,
      fontSize: "0.875rem",
      "&.Mui-focused": {
        color: getMm(isDarkMode).onSurfaceVariant,
      },
    },
    "& .MuiFormHelperText-root": {
      fontFamily: SIGNUP_FONT,
      fontSize: "0.875rem",
    },
  };
}

export function signupDateFieldSx(isDarkMode, hasAgeError) {
  const errorBorder = "#EF4444";
  const normalBorder = getMm(isDarkMode).outlineVariant;
  const borderColor = hasAgeError ? errorBorder : normalBorder;
  const baseField = signupFieldSx(isDarkMode);

  return {
    ...baseField,
    "& .MuiOutlinedInput-root": {
      ...baseField["& .MuiOutlinedInput-root"],
      "& fieldset": {
        borderColor,
        borderWidth: "2px",
      },
      "&:hover fieldset": {
        borderColor,
      },
      "&.Mui-focused fieldset": {
        borderColor,
        borderWidth: "2px",
      },
      "&.Mui-error fieldset": {
        borderColor: errorBorder,
      },
    },
    "& .MuiFormHelperText-root": {
      color: errorBorder,
      fontSize: "0.875rem",
      marginTop: "4px",
      fontFamily: SIGNUP_FONT,
    },
  };
}

export function signupSelectSx(isDarkMode) {
  return {
    width: "100%",
    maxWidth: "100%",
    display: "block",
    borderRadius: "8px",
    backgroundColor: getMm(isDarkMode).surfaceLowest,
    fontFamily: SIGNUP_FONT,
    minHeight: { xs: 48, sm: "auto" },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: getMm(isDarkMode).outlineVariant,
      borderWidth: "2px",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: getMm(isDarkMode).outlineVariant,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: getMm(isDarkMode).outlineVariant,
      borderWidth: "2px",
    },
    "& .MuiSelect-select": {
      width: "100%",
      color: getMm(isDarkMode).onSurface,
      fontFamily: SIGNUP_FONT,
      fontSize: { xs: "1rem", sm: "0.875rem" },
      py: { xs: 1.25, sm: 1 },
      pr: { xs: 4, sm: 4.5 },
    },
  };
}

export function signupPrimaryButtonSx(isDarkMode) {
  return {
    fontFamily: SIGNUP_FONT,
    fontWeight: 600,
    fontSize: "0.875rem",
    textTransform: "none",
    borderRadius: "8px",
    boxShadow: "none",
    backgroundColor: BRAND_PRIMARY.main,
    color: BRAND_PRIMARY.contrastText,
    border: `2px solid ${BRAND_PRIMARY.dark}`,
    "&:hover": {
      backgroundColor: BRAND_PRIMARY.dark,
      borderColor: BRAND_PRIMARY.darker,
      boxShadow: "none",
    },
    "&:disabled": {
      backgroundColor: getMm(isDarkMode).outlineVariant,
      borderColor: getMm(isDarkMode).outlineVariant,
      color: isDarkMode ? "#9CA3AF" : "#9CA3AF",
    },
  };
}

export function signupOutlineButtonSx(isDarkMode) {
  const border = getMm(isDarkMode).outlineVariant;
  const muted = getMm(isDarkMode).onSurfaceVariant;
  return {
    fontFamily: SIGNUP_FONT,
    fontWeight: 600,
    fontSize: "0.875rem",
    textTransform: "none",
    borderRadius: "8px",
    boxShadow: "none",
    color: muted,
    borderColor: border,
    borderWidth: 2,
    backgroundColor: "transparent",
    "&:hover": {
      borderColor: getMm(isDarkMode).outline,
      backgroundColor: getMm(isDarkMode).surfaceContainer,
      boxShadow: "none",
    },
  };
}

export function signupAlertSx(baseAlertSx, isDarkMode) {
  return {
    ...baseAlertSx(isDarkMode),
    fontFamily: SIGNUP_FONT,
    width: "100%",
  };
}

export function signupSummaryBoxSx(isDarkMode) {
  return {
    backgroundColor: getMm(isDarkMode).surfaceLow,
    borderRadius: "8px",
    p: 3,
    border: `1px solid ${getMm(isDarkMode).outlineVariant}`,
    boxShadow: "none",
    fontFamily: SIGNUP_FONT,
  };
}

export function signupTypography(isDarkMode) {
  return {
    brandTitle: {
      fontWeight: 800,
      fontSize: { xs: "1.625rem", sm: "1.875rem" },
      color: getMm(isDarkMode).onSurface,
      fontFamily: SIGNUP_FONT,
      lineHeight: 1.2,
    },
    subtitle: {
      fontSize: "0.875rem",
      color: getMm(isDarkMode).onSurfaceVariant,
      fontFamily: SIGNUP_FONT,
      fontWeight: 400,
      lineHeight: 1.5,
    },
    sectionTitle: {
      fontWeight: 600,
      fontSize: "1.125rem",
      color: getMm(isDarkMode).onSurface,
      fontFamily: SIGNUP_FONT,
    },
    body: {
      color: getMm(isDarkMode).onSurfaceVariant,
      fontSize: "0.875rem",
      lineHeight: 1.6,
      fontFamily: SIGNUP_FONT,
    },
    label: {
      color: getMm(isDarkMode).onSurfaceVariant,
      mb: 1,
      fontWeight: 500,
      fontSize: "0.875rem",
      fontFamily: SIGNUP_FONT,
    },
  };
}
